
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import defensiveCopy from "@/util/defensive-copy";
import { Trigger } from "@/types/conditions";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import DynamicDataFieldText from "@/components/Reusable/ControlsTree/DynamicData/DynamicDataFieldText.vue";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import Icon from "@/components/UI/Icon.vue";
import IconButton from "@/components/UI/IconButton.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import { appendProToLabelInFreeMode } from "@/lib/subscriptions/free-mode";
import { PopupTriggers } from "@/subapps/manage-templates/types";

@Component({
  components: {
    NumberInput,
    Icon,
    CodeInput,
    DynamicDataFieldText,
    TextInput,
    DropdownInput,
    IconButton
  }
})
export default class TriggerInput extends Vue {
  @Prop()
  value!: Trigger;

  @Prop()
  triggers!: PopupTriggers[];

  @Prop({ default: false })
  disableLimit!: boolean;

  get availableTriggersForDropdown(): {
    text: string;
    value: string;
  }[] {
    return this.triggers.map(trigger => ({
      text: trigger.proOnly
        ? appendProToLabelInFreeMode(trigger.text)
        : trigger.text,
      value: trigger.value
    }));
  }

  setValue(value: string | undefined | null) {
    const newTrigger = defensiveCopy(this.value);
    newTrigger.slug = value ?? undefined;
    this.update(newTrigger);
  }

  setDelay(value: number | undefined | null) {
    const newTrigger = defensiveCopy(this.value);
    newTrigger.options = {
      ...newTrigger.options,
      ...{ delay: value ?? undefined }
    };
    this.update(newTrigger);
  }

  setPercent(value: number | undefined | null) {
    const newTrigger = defensiveCopy(this.value);
    newTrigger.options = {
      ...newTrigger.options,
      ...{ percent: value ?? undefined }
    };
    this.update(newTrigger);
  }

  setSelector(value: string | undefined | null) {
    const newTrigger = defensiveCopy(this.value);
    newTrigger.options = {
      ...newTrigger.options,
      ...{ selector: value ?? undefined }
    };
    this.update(newTrigger);
  }

  setScrollType(value: string) {
    const newTrigger = defensiveCopy(this.value);
    newTrigger.options = { ...newTrigger.options, ...{ scrollType: value } };
    this.update(newTrigger);
  }

  get scrollTypeOptions() {
    return [
      { text: "Percent", value: "percent" },
      { text: "Selector", value: "selector" }
    ];
  }

  get scrollTypeValue() {
    return this.value?.options?.scrollType ?? null;
  }

  setClickType(value: string) {
    const newTrigger = defensiveCopy(this.value);
    newTrigger.options = { ...newTrigger.options, ...{ clickType: value } };
    this.update(newTrigger);
  }

  get clickTypeOptions() {
    return [
      { text: "Anywhere", value: "anywhere" },
      { text: "Selector", value: "selector" }
    ];
  }

  get clickTypeValue() {
    return this.value?.options?.clickType ?? null;
  }

  get delayValue() {
    return this.value?.options?.delay ?? null;
  }

  get delayLabel() {
    if (this.value.slug === "inactivity") {
      return "Inactivity length (seconds)";
    }
    return "Show after (seconds)";
  }

  get hasPercentOption() {
    return (
      this.value.slug === "scroll" &&
      this.value.options?.scrollType === "percent"
    );
  }

  get hasDelayOption() {
    return this.value.slug === "load" || this.value.slug === "inactivity";
  }

  get hasSelectorOption() {
    return (
      (this.value.slug === "scroll" &&
        this.value.options?.scrollType === "selector") ||
      (this.value.slug === "click" &&
        this.value.options?.clickType === "selector")
    );
  }

  update(newTrigger: Trigger) {
    this.$emit("input", newTrigger);
  }
}
